import { FC, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import type { MediaId } from 'models/Media';
import { useMediaUrl } from 'hooks/useMediaUrl';

interface Props {
  mediaId: MediaId;
  className?: string;
  // A smaller play glyph, for thumbnails the size of an icon.
  compact?: boolean;
  // Called with the video's natural size once its metadata has loaded.
  onLoadedMetadata?: (event: SyntheticEvent<HTMLVideoElement>) => void;
}

// The first frame of a stored video with a play glyph over it, for grids
// where clicking opens the real player. No controls and no download beyond
// the metadata.
export const MediaVideoThumbnail: FC<Props> = ({
  mediaId,
  className,
  compact = false,
  onLoadedMetadata,
}) => {
  const { url, status, onError } = useMediaUrl(mediaId);

  if (status === 'ready' && url) {
    return (
      <div className={classNames('relative', className)}>
        <video
          preload="metadata"
          muted
          playsInline
          src={`${url}#t=0.001`}
          className="h-full w-full object-cover"
          onError={onError}
          onLoadedMetadata={onLoadedMetadata}
        />
        <span className={compact ? styles.playCompact : styles.play}>
          <FontAwesomeIcon
            icon={faPlay}
            className={compact ? 'ml-px text-[8px]' : 'ml-0.5 text-[15px]'}
            aria-hidden="true"
          />
        </span>
      </div>
    );
  }

  return (
    <div className={classNames(styles.placeholder, className)}>
      <FontAwesomeIcon
        icon={status === 'error' ? faTriangleExclamation : faSpinner}
        className={status === 'error' ? undefined : 'animate-spin'}
        aria-hidden="true"
      />
    </div>
  );
};

const styles = {
  play: classNames(
    'pointer-events-none absolute left-1/2 top-1/2 flex size-10 -translate-x-1/2 -translate-y-1/2',
    'items-center justify-center rounded-full bg-black/45 text-white',
  ),
  playCompact: classNames(
    'pointer-events-none absolute left-1/2 top-1/2 flex size-5 -translate-x-1/2 -translate-y-1/2',
    'items-center justify-center rounded-full bg-black/45 text-white',
  ),
  placeholder:
    'flex items-center justify-center bg-surface-field text-ink-tertiary',
};
